(() => {
  'use strict';
  if (window.galixFaqSystem) return;
  const roots = new Set();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const itemSelector = '.galix-faq-accordion > details';
  const itemsFor = root => [...root.querySelectorAll(itemSelector)];
  const categoryOf = item => item.dataset.category || 'all';
  const cancel = (state, item) => {
    if (state.animation) {
      state.animation.onfinish = null;
      state.animation.cancel();
      state.animation = null;
    }
    item.open = state.open;
    if (state.panel) {
      state.panel.style.height = '';
      state.panel.style.overflow = '';
      state.panel.style.opacity = '';
    }
    item.classList.remove('is-faq-closing');
  };
  function init(root) {
    if (root._galixFaqSystem) return;
    const controller = new AbortController();
    const { signal } = controller;
    const items = itemsFor(root);
    const states = new Map(items.map(item => [item, {
      open: item.open,
      panel: item.querySelector(':scope > div'),
      animation: null,
      version: 0
    }]));
    let filterVersion = 0;
    const animate = (item, open, onDone = () => {}) => {
      const state = states.get(item);
      if (!state || state.open === open) { onDone(); return; }
      const panel = state.panel;
      if (!panel) {
        state.open = open;
        item.open = open;
        onDone();
        return;
      }
      const wasAnimating = Boolean(state.animation);
      const startHeight = wasAnimating
        ? panel.getBoundingClientRect().height
        : state.open ? panel.getBoundingClientRect().height : 0;
      const startOpacity = wasAnimating
        ? (parseFloat(getComputedStyle(panel).opacity) || 0)
        : state.open ? 1 : 0;
      cancel(state, item);
      state.open = open;
      state.version += 1;
      const version = state.version;
      item.open = true;
      panel.style.overflow = 'hidden';
      panel.style.height = `${startHeight}px`;
      panel.style.opacity = String(startOpacity);
      const endHeight = open ? panel.scrollHeight : 0;
      if (reduced.matches || typeof item.animate !== 'function') {
        item.open = open;
        panel.style.height = '';
        panel.style.overflow = '';
        panel.style.opacity = '';
        onDone();
        return;
      }
      const animation = panel.animate([
        { height: `${startHeight}px`, opacity: startOpacity },
        { height: `${endHeight}px`, opacity: open ? 1 : 0 }
      ], {
        duration: open ? 340 : 280,
        easing: 'cubic-bezier(.22, 1, .36, 1)',
        fill: 'both'
      });
      state.animation = animation;
      animation.onfinish = () => {
        if (state.animation !== animation || state.version !== version) return;
        item.open = open;
        panel.style.height = '';
        panel.style.overflow = '';
        panel.style.opacity = '';
        state.animation = null;
        onDone();
      };
      animation.oncancel = () => {};
    };
    items.forEach(item => {
      const summary = item.querySelector('summary');
      const state = states.get(item);
      if (!summary) return;
      summary.addEventListener('click', event => {
        if (reduced.matches) return;
        event.preventDefault();
        const shouldOpen = !state.open;
        if (shouldOpen) items.forEach(other => {
          if (other !== item) animate(other, false);
        });
        animate(item, shouldOpen);
      }, { signal });
      item.addEventListener('toggle', () => { if (!state.animation) state.open = item.open; }, { signal });
    });

    const tabs = [...root.querySelectorAll('[data-faq-category], [data-gx-page-category]')];
    const search = root.querySelector('[data-gx-faq-search]');
    const matching = (item, category, query) => {
      const value = categoryOf(item);
      const categoryMatch = category === 'all'
        ? (root.hasAttribute('data-galix-faq-strip') || root.hasAttribute('data-galix-faq-page') ? value === 'all' || value === 'function' : true)
        : value === category;
      return categoryMatch && (!query || item.textContent.toLocaleLowerCase(document.documentElement.lang || 'es').includes(query));
    };
    const filter = (category = null) => {
      if (category) {
        root.dataset.faqCategoryActive = category;
        tabs.forEach(tab => {
          const active = (tab.dataset.faqCategory || tab.dataset.gxPageCategory) === category;
          tab.classList.toggle('is-active', active);
          if (tab.hasAttribute('aria-pressed')) tab.setAttribute('aria-pressed', String(active));
        });
      }
      const selected = root.dataset.faqCategoryActive || tabs[0]?.dataset.faqCategory ||
        tabs[0]?.dataset.gxPageCategory || 'all';
      const query = (search?.value || '').trim().toLocaleLowerCase(document.documentElement.lang || 'es');
      const version = ++filterVersion;
      const matchingItems = [];
      items.forEach(item => {
        const state = states.get(item);
        const visible = matching(item, selected, query);
        cancel(state, item);
        if (visible) {
          item.hidden = false;
          matchingItems.push(item);
        } else if (state.open) {
          item.hidden = false;
          animate(item, false, () => { if (filterVersion === version) item.hidden = true; });
        } else {
          item.hidden = true;
        }
      });
      const empty = root.querySelector('[data-gx-faq-empty]');
      if (empty) empty.hidden = matchingItems.length > 0;
    };
    tabs.forEach(tab => tab.addEventListener('click', () => {
      filter(tab.dataset.faqCategory || tab.dataset.gxPageCategory || 'all');
    }, { signal }));
    search?.addEventListener('input', () => filter(), { signal });
    filter();
    const destroy = () => {
      controller.abort();
      items.forEach(item => cancel(states.get(item), item));
      roots.delete(root);
      delete root._galixFaqSystem;
    };
    root._galixFaqSystem = { filter, destroy, states };
    roots.add(root);
  }
  function scan(scope = document) {
    if (scope.matches?.('[data-galix-faq]')) init(scope);
    scope.querySelectorAll?.('[data-galix-faq]').forEach(init);
  }
  window.galixFaqSystem = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    roots.forEach(root => { if (root === event.target || event.target.contains(root)) root._galixFaqSystem?.destroy(); });
  });
  reduced.addEventListener('change', () => {
    roots.forEach(root => itemsFor(root).forEach(item => {
      const state = root._galixFaqSystem?.states.get(item);
      if (!state) return;
      cancel(state, item);
      item.open = state.open;
    }));
  });
})();
